import React, { useEffect, useMemo, useState } from "react";
import { copyText } from "@/lib/clipboard";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Copy, Check, Maximize2, ImageOff, Star, CalendarClock, Lock } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useResourceList, useResourceMutations } from "@/hooks/useResource";
import { useOptions } from "@/hooks/useOptions";
import { userOptions } from "@/lib/users";
import { cleanOfferName, fmtDate, flagEmoji, geoName } from "@/lib/format";
import { FormDrawer } from "@/components/kit/FormDrawer";
import { TextField, Field, SelectField } from "@/components/kit/fields";
import { SearchSelect } from "@/components/kit/SearchSelect";
import { usePlatformTrackingDomains } from "@/lib/domains";
import { assetUrl } from "@/lib/branding";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const empty = { name: "", offer_id: "", stream_key: "", buyer_id: "", tracking_domain: "", tracking_domain_override: false, landing: "" };

const fmtOffer = (o) => `#${o.tenant_seq} ${cleanOfferName(o.name)}${o.geo ? ` – [${String(o.geo).toUpperCase()}]` : ""}`;

export function StreamDrawer({ open, item, onOpenChange }) {
  const { user } = useAuth();
  const opts = useOptions();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isBuyer = user.role === "buyer";
  const { data: offersData } = useResourceList("offers", {});
  const { create, update } = useResourceMutations("streams");
  const [form, setForm] = useState(empty);
  const isEdit = !!item?.id;
  // Owner is locked once the stream has traffic — changing the Buyer would mean moving history.
  const ownerLocked = isEdit && Number(item?.clicks || 0) > 0;
  const offers = offersData?.items || [];

  useEffect(() => { if (open) setForm(item ? { ...empty, ...item } : empty); }, [open, item]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Resolve the offer directly so preselection works even outside the buyer's scoped list.
  const { data: singleOffer } = useQuery({
    queryKey: ["offer", form.offer_id],
    queryFn: async () => (await http.get(`/offers/${form.offer_id}`)).data,
    enabled: !!form.offer_id,
  });
  const selectedOffer = useMemo(() => offers.find((o) => o.id === form.offer_id) || singleOffer || null, [offers, form.offer_id, singleOffer]);
  // Single source of truth: Primary Platform Domain (+ dedicated tracking subdomain) + tenant's
  // own tracking domains — used only to render link previews.
  const td = usePlatformTrackingDomains();
  const PLACEHOLDER = new Set(["", "track.affilix.io", "panel.affilix.io", "affilix.io"]);
  // A stream may ONLY use a tracking domain the GEO offer allows.
  const offerAllowed = useMemo(() => {
    const raw = (selectedOffer?.tracking_domains && selectedOffer.tracking_domains.length)
      ? selectedOffer.tracking_domains
      : (selectedOffer?.tracking_domain ? [selectedOffer.tracking_domain] : []);
    return Array.from(new Set(raw.map((d) => (d || "").toLowerCase()).filter((d) => d && !PLACEHOLDER.has(d))));
  }, [selectedOffer]); // eslint-disable-line
  const offerDefault = useMemo(() => {
    const d = (selectedOffer?.tracking_domain || "").toLowerCase();
    return (d && offerAllowed.includes(d)) ? d : (offerAllowed[0] || td.defaultDomain || "");
  }, [selectedOffer, offerAllowed, td.defaultDomain]);
  // The stream can override only when the offer offers a real choice (2+ allowed domains).
  const canOverride = offerAllowed.length > 1;

  // If not overriding (or no valid override), the stream inherits the offer's current default.
  const overrideOn = canOverride && form.tracking_domain_override && offerAllowed.includes((form.tracking_domain || "").toLowerCase());
  const effectiveTd = overrideOn ? (form.tracking_domain || "").toLowerCase() : offerDefault;

  const offerOptions = useMemo(() => {
    const mk = (o) => ({ value: o.id, label: fmtOffer(o),
      search: `#${o.tenant_seq} ${o.tenant_seq} ${o.name || ""} ${o.geo || ""} ${o.advertiser_name || ""} ${o.product_name || ""}` });
    const base = offers.map(mk);
    if (selectedOffer && !base.some((o) => o.value === selectedOffer.id)) base.unshift(mk(selectedOffer));
    return base;
  }, [offers, selectedOffer]);
  const buyerOptions_ = useMemo(() => userOptions(opts.users), [opts.users]);

  const selectedTd = effectiveTd;
  const previewLink = td.linkFor(selectedTd, isEdit ? (form.stream_key || item?.stream_key || "") : "<auto-generated>");

  // Landings the offer exposes for selection — ACTIVE only, and Public OR Private-with-access for
  // the stream's buyer (inactive/forbidden stay working via backend fallback but are hidden here).
  const offerLandings = useMemo(
    () => (selectedOffer?.landings || []).filter((l) => {
      if (!l.name || l.status === "inactive") return false;
      if ((l.visibility || "public") === "private") return (l.allowed_buyers || []).includes(form.buyer_id);
      return true;
    }),
    [selectedOffer, form.buyer_id]);
  const hasOfferLandings = (selectedOffer?.landings || []).some((l) => l.name);
  const isMultiGeo = selectedOffer?.landing_mode === "multi_geo";

  const save = async () => {
    if (!form.offer_id) return toast.error("Select a GEO offer");
    if (!isBuyer && !ownerLocked && !form.buyer_id) return toast.error("Select a buyer for this stream");
    if (isEdit && form.stream_key && !/^[A-Za-z0-9_-]{4,40}$/.test(String(form.stream_key).trim()))
      return toast.error("Stream key must be 4–40 chars: letters, numbers, - or _");
    const body = { name: form.name, offer_id: form.offer_id };
    if (!ownerLocked) body.buyer_id = isBuyer ? user.id : form.buyer_id;
    body.tracking_domain_override = overrideOn;
    body.tracking_domain = overrideOn ? form.tracking_domain : "";
    body.landing = isMultiGeo ? "" : (form.landing || "");
    if (isEdit) {
      body.stream_key = form.stream_key;
      await update.mutateAsync({ id: item.id, body });
      // Manual status goes through the dedicated endpoint — the one source of truth that
      // enforces the offer dependency rules (same as bulk edit and the user-streams popup).
      const desired = (form.status || "active") === "paused" ? "disabled" : "active";
      const current = (item.status || "active") === "paused" ? "disabled" : "active";
      if (!isBuyer && desired !== current) {
        try {
          const { data: res } = await http.post("/streams/bulk-status", { ids: [item.id], status: desired });
          if (desired === "active" && (res.skipped || []).length) {
            toast.error("Stream cannot be activated", {
              description: `Stream cannot be activated because ${res.skipped[0].reason}. Enable the offer first.`,
            });
          }
        } catch (e) {
          toast.error("Status change failed", { description: e?.response?.data?.detail || "Try again." });
        }
        qc.invalidateQueries({ queryKey: ["streams"] });
      }
    } else { await create.mutateAsync(body); navigate("/streams"); }
    onOpenChange(false);
  };

  return (
    <FormDrawer open={open} onOpenChange={onOpenChange} title={isEdit ? "Edit Stream" : "New Stream"}
      subtitle={isEdit ? form.name : "A stream is just a tracking link to a GEO offer"} size="lg"
      onSave={save} saving={create.isPending || update.isPending} saveLabel={isEdit ? "Save changes" : "Create stream"} testId="stream-drawer">
      <div className="space-y-5">
        <TextField label="Stream name (optional)" value={form.name} onChange={(v) => set("name", v)} testId="stream-name" placeholder="Leave empty → auto Stream #ID" />
        {!isBuyer && isEdit && (
          <Field label="Status" hint="Manual switch. A stream can be activated only when its GEO offer is active — disabling the offer later blocks traffic automatically, but won't flip this switch back.">
            <SelectField value={(form.status || "active") === "paused" ? "disabled" : "active"}
              onChange={(v) => set("status", v === "disabled" ? "paused" : "active")}
              options={[{ value: "active", label: "Active" }, { value: "disabled", label: "Disabled" }]} testId="stream-status-select" />
            {selectedOffer && selectedOffer.status !== "active" && (form.status || "active") !== "paused" && (
              <p className="mt-1.5 rounded-md border border-[#EAB308]/30 bg-[#EAB308]/[0.07] px-2 py-1.5 text-xs text-[#EAB308]" data-testid="stream-status-offer-warning">
                The linked offer is disabled — the stream cannot be activated until the offer is enabled.
              </p>
            )}
          </Field>
        )}
        {!isBuyer && !ownerLocked && (
          <Field label="Buyer" hint="The stream will appear in this buyer's /streams.">
            <SearchSelect value={form.buyer_id} onChange={(v) => set("buyer_id", v)} options={buyerOptions_}
              placeholder="Select buyer" searchPlaceholder="Search by name, email or ID..." testId="stream-buyer" widthClass="w-[360px]" />
          </Field>
        )}
        {!isBuyer && ownerLocked && (
          <Field label="Buyer" hint="Owner is locked — this stream already has recorded traffic. To route traffic to another buyer, create a new stream.">
            <div className="flex items-center gap-2 rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-3 py-2.5" data-testid="stream-buyer-locked">
              <span className="text-sm text-foreground">{item?.buyer_seq != null ? `#${item.buyer_seq} ` : ""}{item?.buyer_name || "—"}</span>
              <Lock className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
            </div>
          </Field>
        )}
        <Field label="GEO Offer"
          hint={isBuyer ? "Public offers and offers assigned to you (same list as your Offers page)." : "Swap anytime — the same stream key & link keep working, traffic just routes to the new GEO offer."}>
          <SearchSelect value={form.offer_id} onChange={(v) => { set("offer_id", v); }} options={offerOptions}
            placeholder="Select GEO offer" searchPlaceholder="Search by #ID, name, GEO, advertiser, product..." testId="stream-offer" widthClass="w-[420px]" />
        </Field>
        {form.offer_id && offerAllowed.length > 0 && (
          <Field label="Tracking domain" hint="Inherited from the GEO offer. New offer tracking domain? Non-overridden streams update automatically.">
            <div className="space-y-2">
              <div className="flex items-center gap-2 rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-3 py-2.5">
                <span className="text-xs text-muted-foreground">Offer default:</span>
                <code className="flex-1 font-mono text-xs text-foreground" data-testid="stream-offer-default-domain">{offerDefault || "—"}</code>
              </div>
              {canOverride && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input type="checkbox" checked={!!form.tracking_domain_override} data-testid="stream-override-toggle"
                    onChange={(e) => set("tracking_domain_override", e.target.checked)} className="h-3.5 w-3.5 accent-white" />
                  Override for this stream (choose a specific domain the offer allows)
                </label>
              )}
              {canOverride && form.tracking_domain_override && (
                <SelectField value={offerAllowed.includes((form.tracking_domain || "").toLowerCase()) ? form.tracking_domain : offerAllowed[0]}
                  onChange={(v) => set("tracking_domain", v)} options={offerAllowed} testId="stream-tracking-domain" />
              )}
            </div>
          </Field>
        )}
        {form.offer_id && isMultiGeo && (
          <Field label="Multi-GEO routing" hint="This offer routes visitors automatically by their country — the stream can't pin a specific landing.">
            <MultiGeoRouting landings={selectedOffer?.geo_landings || []} />
          </Field>
        )}
        {form.offer_id && !isMultiGeo && offerLandings.length > 0 && (
          <Field label="Landing" hint="Pick a landing or leave on the offer default. Preview before choosing.">
            <LandingPicker landings={offerLandings} value={form.landing} onChange={(v) => set("landing", v)} />
          </Field>
        )}
        {form.offer_id && !isMultiGeo && offerLandings.length === 0 && (
          <div className="rounded-lg border border-[#EAB308]/30 bg-[#EAB308]/[0.07] px-3 py-2.5 text-xs text-[#EAB308]" data-testid="stream-no-landings">
            {hasOfferLandings
              ? "No landing is available to this buyer right now (all inactive, or Private without access). The stream will fall back to the offer's default landing. Activate a Public landing or grant access in the offer's Landing tab."
              : "This offer has no landings yet. Add one in the offer's Landing tab before driving traffic."}
          </div>
        )}
        {isEdit && (
          <TextField label="Stream key" value={form.stream_key} onChange={(v) => set("stream_key", v)} testId="stream-key-input"
            hint="4–40 chars: letters, numbers, - or _. Must be unique. Changing it updates the tracking link." />
        )}
        <Field label="Tracking link">
          <div className="flex items-center gap-2 rounded-lg border border-white/[0.1] bg-[#0A0A0A] px-3 py-2.5">
            <code className="flex-1 truncate font-mono text-xs text-muted-foreground" data-testid="stream-tracking-link">{previewLink}</code>
            {isEdit && (
              <button onClick={() => copyText(previewLink)} className="text-muted-foreground hover:text-foreground">
                <Copy className="h-4 w-4" />
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground/70">{isMultiGeo ? "Destination is chosen automatically by visitor GEO (Multi-GEO)" : (form.landing ? "Using the selected landing above." : "Using the offer's default landing")}{!isEdit ? "; a unique key is generated on save." : "."}</p>
        </Field>
      </div>
    </FormDrawer>
  );
}

// Read-only Multi-GEO routing preview: same card format as the Classic landing picker
// (preview image, name, URL, GEO badge) but without any Choose/switch controls — the
// destination is decided by the visitor's country, never by the stream.
function MultiGeoRouting({ landings }) {
  const [preview, setPreview] = useState(null);
  const live = (landings || []).filter((l) => l.status !== "inactive");
  const geos = live.filter((l) => !l.fallback);
  const fb = live.find((l) => l.fallback);
  const badge = (l) => l.fallback ? "🌐 All other GEOs · FALLBACK" : `${flagEmoji(l.geo)} ${(l.geo || "").toUpperCase()} · ${geoName(l.geo)}`;
  const Card = ({ l }) => {
    const img = assetUrl(l.preview_image);
    return (
      <div className={cn("group relative w-52 shrink-0 overflow-hidden rounded-xl border bg-[#0A0A0A] transition-all hover:border-white/20", l.fallback ? "border-[#3B82F6]/30" : "border-white/[0.08]")} data-testid={`multigeo-row-${l.id}`}>
        <button type="button" onClick={() => setPreview(l)} data-testid={`multigeo-open-${l.id}`}
          className="relative block h-28 w-full cursor-zoom-in overflow-hidden bg-[#141417]" title="Click to preview">
          {img ? <img src={img} alt={l.name} className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]" />
            : <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground"><ImageOff className="h-5 w-5" /><span className="text-[10px]">No preview — click to view details</span></div>}
          <span className="absolute right-2 top-2 rounded-md bg-black/60 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100"><Maximize2 className="h-3.5 w-3.5" /></span>
        </button>
        <div className="p-3">
          <div className="truncate text-sm text-foreground">{l.name || l.url}</div>
          {l.url && <div className="truncate text-[11px] text-muted-foreground">{l.url}</div>}
          <span className={cn("mt-1.5 inline-flex max-w-full items-center truncate rounded px-1.5 py-0.5 text-[10px] font-medium", l.fallback ? "bg-[#3B82F6]/15 text-[#3B82F6]" : "bg-white/[0.06] text-foreground")} data-testid={`multigeo-geo-${l.id}`}>
            {badge(l)}
          </span>
          {l.expires_at && <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground"><CalendarClock className="h-2.5 w-2.5" /> Expires {fmtDate(l.expires_at)}</div>}
        </div>
      </div>
    );
  };
  return (
    <div className="space-y-2" data-testid="stream-multigeo-routing">
      {(geos.length > 0 || fb) && (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {geos.map((l) => <Card key={l.id} l={l} />)}
          {fb && <Card l={fb} />}
        </div>
      )}
      {!fb && <p className="rounded-md border border-[#EAB308]/30 bg-[#EAB308]/[0.07] px-2 py-1.5 text-xs text-[#EAB308]">No Fallback landing configured yet.</p>}
      {geos.length === 0 && !fb && <p className="text-xs text-muted-foreground">No active Multi-GEO landings yet.</p>}
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-3xl border-white/[0.1] bg-[#0A0A0A]" data-testid="multigeo-preview-dialog">
          <DialogTitle className="sr-only">Landing preview</DialogTitle>
          {preview && (
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  {preview.name || preview.url}
                  <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium", preview.fallback ? "bg-[#3B82F6]/15 text-[#3B82F6]" : "bg-white/[0.06] text-foreground")}>
                    {badge(preview)}
                  </span>
                </div>
                {preview.url && <div className="break-all text-xs text-muted-foreground">{preview.url}</div>}
                {preview.expires_at && (
                  <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground"><CalendarClock className="h-3 w-3" /> Expires {fmtDate(preview.expires_at)}</div>
                )}
              </div>
              <div className="overflow-hidden rounded-lg border border-white/[0.08]">
                {assetUrl(preview.preview_image)
                  ? <img src={assetUrl(preview.preview_image)} alt={preview.name} className="max-h-[60vh] w-full object-contain" />
                  : <div className="flex h-64 items-center justify-center text-muted-foreground">No preview image uploaded for this landing.</div>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LandingCard({ l, selected, onSelect, onPreview }) {
  const img = assetUrl(l.preview_image);
  return (
    <div className={cn("group relative w-52 shrink-0 overflow-hidden rounded-xl border bg-[#0A0A0A] transition-all", selected ? "border-[#22C55E] ring-1 ring-[#22C55E]/40" : "border-white/[0.08] hover:border-white/20")} data-testid={`landing-card-${l.id}`}>
      <button type="button" onClick={() => onPreview(l)} data-testid={`landing-open-${l.id}`}
        className="relative block h-28 w-full cursor-zoom-in overflow-hidden bg-[#141417]" title="Click to preview">
        {img ? <img src={img} alt={l.name} className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]" />
          : <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground"><ImageOff className="h-5 w-5" /><span className="text-[10px]">No preview — click to view details</span></div>}
        <span className="absolute right-2 top-2 rounded-md bg-black/60 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100"><Maximize2 className="h-3.5 w-3.5" /></span>
        {selected && <span className="absolute left-2 top-2 flex items-center gap-1 rounded-md bg-[#22C55E] px-1.5 py-0.5 text-[10px] font-medium text-black"><Check className="h-3 w-3" /> Selected</span>}
      </button>
      <div className="p-3">
        <div className="flex items-center gap-1.5 truncate text-sm text-foreground">
          {l.name}
          {l.primary && <span className="inline-flex items-center gap-0.5 rounded bg-[#EAB308]/15 px-1 py-0.5 text-[9px] font-semibold text-[#EAB308]"><Star className="h-2.5 w-2.5" fill="#EAB308" /> DEFAULT</span>}
        </div>
        {l.url && <div className="truncate text-[11px] text-muted-foreground">{l.url}</div>}
        {l.expires_at && <div className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground"><CalendarClock className="h-2.5 w-2.5" /> Expires {fmtDate(l.expires_at)}</div>}
        <button type="button" onClick={() => onSelect(selected ? "" : l.name)} data-testid={`landing-choose-${l.id}`}
          className={cn("mt-2 w-full rounded-md py-1.5 text-xs font-medium transition-colors", selected ? "bg-white/[0.06] text-muted-foreground hover:text-foreground" : "bg-white text-black hover:bg-white/90")}>
          {selected ? "Chosen" : "Choose"}
        </button>
      </div>
    </div>
  );
}

function LandingPicker({ landings, value, onChange }) {
  const [preview, setPreview] = useState(null);
  return (
    <div className="space-y-2" data-testid="landing-picker">
      <div className="flex gap-3 overflow-x-auto pb-2">
        {landings.map((l) => (
          <LandingCard key={l.id} l={l} selected={value === l.name} onSelect={onChange} onPreview={setPreview} />
        ))}
      </div>
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-3xl border-white/[0.1] bg-[#0A0A0A]" data-testid="landing-preview-dialog">
          <DialogTitle className="sr-only">Landing preview</DialogTitle>
          {preview && (
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  {preview.name}
                  <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-medium capitalize", preview.status === "inactive" ? "bg-white/[0.06] text-muted-foreground" : "bg-[#22C55E]/15 text-[#22C55E]")} data-testid="landing-preview-status">
                    {preview.status || "active"}
                  </span>
                </div>
                <div className="break-all text-xs text-muted-foreground">{preview.url}</div>
                {preview.expires_at && (
                  <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground"><CalendarClock className="h-3 w-3" /> Expires {fmtDate(preview.expires_at)}</div>
                )}
              </div>
              <div className="overflow-hidden rounded-lg border border-white/[0.08]">
                {assetUrl(preview.preview_image)
                  ? <img src={assetUrl(preview.preview_image)} alt={preview.name} className="max-h-[60vh] w-full object-contain" />
                  : <div className="flex h-64 items-center justify-center text-muted-foreground">No preview image uploaded for this landing.</div>}
              </div>
              <button type="button" onClick={() => { onChange(preview.name); setPreview(null); }} data-testid="landing-preview-choose"
                className="w-full rounded-md bg-white py-2 text-sm font-medium text-black hover:bg-white/90">Choose this landing</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
